/*
 * Size guide (v2)
 * ---------------
 * Builds the size chart tables (Body / Garment tabs, IN / CM toggle), the
 * how-to-measure list, and the stepped size finder.
 *
 * Measurements come from assets/size-charts.json, generated from the brand's
 * master workbook. Values are rendered exactly as supplied.
 *
 * The panel lives inside the variant picker, which the theme destroys and
 * rebuilds on every variant change. It is therefore a custom element with no
 * external state: connectedCallback rebuilds it from the saved profile, so a
 * mid-flow size change can never leave it half-wired.
 *
 * Pure logic is exported so scripts/test-size-logic.mjs can verify it outside
 * a browser.
 */

/* ------------------------------------------------------------------ *
 * Pure logic
 * ------------------------------------------------------------------ */

export const LIMITS = {
  heightCm: { min: 120, max: 220 },
  heightFt: { min: 3, max: 7 },
  heightIn: { min: 0, max: 11 },
  weightKg: { min: 35, max: 200 },
  weightLb: { min: 77, max: 440 }
};

/**
 * Parse a whole number. Every field in the finder is whole numbers only —
 * a tenth of a centimetre or a pound cannot change which size is suggested,
 * and allowing decimals only invites malformed entries like "5.9" for height.
 * Anything that is not purely digits is rejected rather than coerced.
 */
export function parseInteger(input) {
  // Negatives are rejected in both branches. The string branch never matched a
  // minus sign, so accepting -72 from the number branch was an inconsistency.
  if (typeof input === 'number') return Number.isInteger(input) && input >= 0 ? input : null;
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  // parseInt returns Infinity past ~309 digits. The range checks would reject it
  // anyway, but a function that promises a whole number should not hand one back.
  const value = parseInt(trimmed, 10);
  return Number.isFinite(value) ? value : null;
}

export const inToCm = (inches) => inches * 2.54;
export const cmToIn = (cm) => cm / 2.54;
export const lbToKg = (lb) => lb * 0.45359237;
export const kgToLb = (kg) => kg / 0.45359237;

/** Feet and inches to centimetres. */
export function feetInchesToCm(feet, inches) {
  return inToCm(feet * 12 + inches);
}

/** Centimetres to whole feet and inches, carrying 12in up to a foot. */
export function cmToFeetInches(cm) {
  const totalInches = Math.round(cmToIn(cm));
  return { feet: Math.floor(totalInches / 12), inches: totalInches % 12 };
}

/** Round for display: one decimal, but drop a trailing .0 */
export function tidy(value, decimals = 1) {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return Number(value.toFixed(decimals));
}

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/*
 * Anchor points carried over from the theme's existing recommender
 * (GENERIC_RANGES / BODY_MEASUREMENTS_BY_SIZE in assets/size-recommender.js),
 * which were already tuned for this brand. Interpolating between them keeps
 * that calibration instead of inventing a new regression.
 */
const ANCHORS = {
  weightKg: [52, 60, 69, 80, 91, 105, 120],
  heightCm: [163, 168, 173, 180, 186, 192, 197],
  chestCm: [86, 92, 98, 104, 110, 117, 125],
  waistCm: [68, 74, 80, 87, 94, 102, 110],
  hipCm: [88, 94, 100, 106, 112, 119, 127]
};

/** Position of `target` within an ascending array, as a fractional index. */
export function fractionalIndex(values, target) {
  const points = [];
  values.forEach((value, index) => {
    if (typeof value === 'number' && Number.isFinite(value)) points.push({ value, index });
  });
  if (!points.length) return null;
  if (target <= points[0].value) return points[0].index;

  for (let i = 0; i < points.length - 1; i += 1) {
    const low = points[i];
    const high = points[i + 1];
    if (target <= high.value) {
      const span = high.value - low.value;
      const ratio = span === 0 ? 0 : (target - low.value) / span;
      return low.index + ratio * (high.index - low.index);
    }
  }
  return points[points.length - 1].index;
}

/** Interpolate a value out of an anchor array at a fractional index. */
function atIndex(values, index) {
  const clamped = Math.max(0, Math.min(values.length - 1, index));
  const low = Math.floor(clamped);
  const high = Math.ceil(clamped);
  if (low === high) return values[low];
  return values[low] + (values[high] - values[low]) * (clamped - low);
}

/* Index shift per cm of height away from the height typical at a given weight,
   and the most it may ever shift. Tuned so ordinary builds land where they did
   before while atypical ones are corrected. */
const HEIGHT_CORRECTION_PER_CM = 0.06;
const MAX_HEIGHT_CORRECTION = 1.5;

/** Estimate body measurements (cm) from height and weight. */
export function estimateBody(heightCm, weightKg) {
  const weightIndex = fractionalIndex(ANCHORS.weightKg, weightKg);

  // The anchors pair a height WITH a weight (a size-L man is both 180cm and
  // 80kg), so height must be read as a deviation from what is typical at this
  // weight, not added on its own. Taller than typical for the weight means a
  // slimmer build; shorter means a heavier one. Adding both positively would
  // rank a 200cm/80kg body as larger than a 160cm/80kg body.
  const expectedHeight = atIndex(ANCHORS.heightCm, weightIndex);
  const correction = clamp(
    (heightCm - expectedHeight) * HEIGHT_CORRECTION_PER_CM,
    -MAX_HEIGHT_CORRECTION,
    MAX_HEIGHT_CORRECTION
  );
  const index = weightIndex - correction;

  return {
    chest: atIndex(ANCHORS.chestCm, index),
    waist: atIndex(ANCHORS.waistCm, index),
    hip: atIndex(ANCHORS.hipCm, index)
  };
}

const CHEST_SHIFT_CM = { narrow: -3, medium: 0, wide: 4 };
const ABDOMEN_SHIFT_CM = { flat: -3, medium: 0, full: 6 };

/** Apply the body-shape answers to an estimate. */
export function applyShape(body, chestShape, abdomenShape) {
  return {
    chest: body.chest + (CHEST_SHIFT_CM[chestShape] ?? 0),
    waist: body.waist + (ABDOMEN_SHIFT_CM[abdomenShape] ?? 0),
    hip: body.hip + (ABDOMEN_SHIFT_CM[abdomenShape] ?? 0) * 0.5
  };
}

export const FIT_OFFSETS = {
  very_fitted: -1,
  fitted: -0.5,
  regular: 0,
  loose: 0.5,
  very_loose: 1
};

/** The measurement a chart is sized by, in order of preference. */
export function fittingRow(chart) {
  const rows = (chart.rows && chart.rows.body) || Object.keys(chart.body || {});
  const order = chart.category === 'bottom' ? ['Waist', 'Hip', 'Chest'] : ['Chest', 'Waist', 'Hip'];
  return order.find((name) => rows.includes(name)) || rows[0] || null;
}

/**
 * Recommend a size.
 *
 * Matching is driven by the BODY table -- the brand's own statement of which
 * body fits which size -- then nudged by the customer's fit preference. The
 * garment table is reported alongside for context rather than used to compute
 * the answer: garment ease differs wildly by category (a shirt carries ~6in
 * over the body, a trouser waist is 1:1), and parts of it are known to be
 * incomplete, so driving the recommendation from it would be fragile.
 */
export function recommendSize(chart, { heightCm, weightKg, chestShape, abdomenShape, fit, availableSizes } = {}) {
  if (!chart || !Array.isArray(chart.sizes) || !chart.sizes.length) {
    return { error: 'no-chart' };
  }

  const row = fittingRow(chart);
  const bodyValues = row && chart.body ? chart.body[row] : null;
  if (!row || !Array.isArray(bodyValues)) return { error: 'no-body-data' };

  const estimate = applyShape(estimateBody(heightCm, weightKg), chestShape, abdomenShape);
  const measurementCm = row === 'Waist' ? estimate.waist : row === 'Hip' ? estimate.hip : estimate.chest;
  // Callers validate first, so this only fires if something upstream broke. Say
  // we can't help rather than confidently naming the largest size, which is what
  // a NaN estimate would otherwise land on.
  if (!Number.isFinite(measurementCm)) return { error: 'no-body-data' };
  const measurementIn = cmToIn(measurementCm);

  const baseIndex = fractionalIndex(bodyValues, measurementIn);
  if (baseIndex === null) return { error: 'no-body-data' };

  const offset = FIT_OFFSETS[fit] ?? 0;
  let index = Math.round(baseIndex + offset);
  index = Math.max(0, Math.min(chart.sizes.length - 1, index));

  // Keep the answer to sizes this product actually sells.
  const offered = Array.isArray(availableSizes) && availableSizes.length
    ? chart.sizes
        .map((size, position) => ({ size, position }))
        .filter((entry) => availableSizes.some((value) => String(value).trim().toLowerCase() === entry.size.toLowerCase()))
    : chart.sizes.map((size, position) => ({ size, position }));

  if (!offered.length) return { error: 'no-sizes' };

  let chosen = offered[0];
  offered.forEach((entry) => {
    if (Math.abs(entry.position - index) < Math.abs(chosen.position - index)) chosen = entry;
  });

  const garmentValues = chart.garment ? chart.garment[row] : null;
  const garmentValue = Array.isArray(garmentValues) ? garmentValues[chosen.position] : null;
  const room = typeof garmentValue === 'number' ? garmentValue - measurementIn : null;

  return {
    size: chosen.size,
    row,
    measurementIn: tidy(measurementIn),
    measurementCm: tidy(measurementCm),
    garmentIn: typeof garmentValue === 'number' ? garmentValue : null,
    roomIn: room === null ? null : tidy(room),
    adjusted: chosen.position !== index
  };
}

/**
 * Validate step one.
 *
 * Accepts either a centimetres value or a feet + inches pair, plus a weight in
 * kilograms or pounds. Returns { ok, heightCm, weightKg } or { ok: false, message }.
 */
export function validateMeasurements({ heightUnit, heightCm, heightFt, heightIn, weightUnit, weight } = {}) {
  let cm = null;

  if (heightUnit === 'ftin') {
    const feet = parseInteger(heightFt);
    // Inches may legitimately be left blank at an exact foot.
    const inches = String(heightIn ?? '').trim() === '' ? 0 : parseInteger(heightIn);

    if (feet === null || inches === null) {
      return { ok: false, message: 'Enter your height in feet and inches.' };
    }
    if (inches > LIMITS.heightIn.max) {
      return { ok: false, message: 'Inches should be between 0 and 11.' };
    }
    if (feet < LIMITS.heightFt.min || feet > LIMITS.heightFt.max) {
      return { ok: false, message: 'Please check your height.' };
    }
    cm = feetInchesToCm(feet, inches);
  } else {
    const value = parseInteger(heightCm);
    if (value === null) return { ok: false, message: 'Enter your height in centimetres.' };
    cm = value;
  }

  if (cm < LIMITS.heightCm.min || cm > LIMITS.heightCm.max) {
    return { ok: false, message: 'Please check your height.' };
  }

  const weightValue = parseInteger(weight);
  if (weightValue === null) {
    return { ok: false, message: weightUnit === 'lb' ? 'Enter your weight in pounds.' : 'Enter your weight in kilograms.' };
  }
  const kg = weightUnit === 'lb' ? lbToKg(weightValue) : weightValue;
  if (kg < LIMITS.weightKg.min || kg > LIMITS.weightKg.max) {
    return { ok: false, message: 'Please check your weight.' };
  }

  return { ok: true, heightCm: cm, weightKg: kg };
}

/* How to measure. Body phrasing where the customer measures themselves,
   garment phrasing for rows that only exist on the garment table. */
export const MEASURE_HELP = {
  Chest: 'Measure around the fullest part of your chest, keeping the tape level and under your arms.',
  Waist: 'Measure around your natural waistline, just above the hip bone, with the tape comfortably loose.',
  Hip: 'Measure around the fullest part of your hips, roughly 20cm below your waist.',
  Neck: 'Measure around the base of your neck where a collar sits, leaving room for one finger.',
  Shoulder: 'Measure across your back, from the edge of one shoulder to the edge of the other.',
  Sleeve: 'Measure from the shoulder edge, down a slightly bent arm, to the wrist.',
  Armhole: 'Garment measurement: taken around the arm opening, from the shoulder seam down to the underarm and back.',
  Length: 'Garment measurement: taken from the highest point of the shoulder straight down to the hem.',
  'Full Length': 'Garment measurement: taken from the top of the waistband down to the hem.',
  Thigh: 'Garment measurement: taken across the leg at its widest point below the crotch seam.',
  Hem: 'Garment measurement: taken across the leg opening.'
};

/* ------------------------------------------------------------------ *
 * DOM
 * ------------------------------------------------------------------ */

const PROFILE_KEY = 'draftxx:size-profile';
let chartsPromise = null;

function loadCharts(url) {
  if (!chartsPromise) {
    chartsPromise = fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('size charts unavailable');
        return response.json();
      })
      .catch((error) => {
        chartsPromise = null;
        throw error;
      });
  }
  return chartsPromise;
}

function readProfile() {
  try {
    return JSON.parse(window.localStorage.getItem(PROFILE_KEY)) || {};
  } catch (error) {
    return {};
  }
}

function writeProfile(profile) {
  try {
    window.localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch (error) {
    /* Safari private mode throws on write; the finder still works this session. */
  }
}

/* Falls back to a plain class outside the browser so the pure logic above can
   be imported by scripts/test-size-logic.mjs. */
const ElementBase = typeof HTMLElement !== 'undefined' ? HTMLElement : class {};

class SizeGuide extends ElementBase {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    this.table = 'body';
    this.unit = 'in';
    this.heightUnit = 'cm';
    this.weightUnit = 'kg';
    this.profile = readProfile();

    this.bindTabs();
    this.bindFinder();

    // The drawer markup sits in the page from first paint, so loading here
    // would pull the chart data on every product view. Instead: warm it the
    // moment the customer shows intent (hover, touch or keyboard focus on the
    // Size Guide button), and load on open as the backstop. Not `once` — a
    // failed fetch must be retried when they open it again.
    const drawer = this.closest('x-drawer');
    if (!drawer) {
      this.load();
      return;
    }

    drawer.addEventListener('dialog:before-show', () => this.load());

    if (drawer.id) {
      const warm = () => this.load();
      document.querySelectorAll('[aria-controls]').forEach((control) => {
        if (control.getAttribute('aria-controls') !== drawer.id) return;
        control.addEventListener('pointerenter', warm, { once: true });
        control.addEventListener('touchstart', warm, { once: true, passive: true });
        control.addEventListener('focus', warm, { once: true });
      });
    }
  }

  async load() {
    // Safe to call repeatedly: warming, opening and reopening all route here.
    if (this.chart || this.loading) return;
    this.loading = true;

    const wrap = this.querySelector('[data-table-wrap]');
    try {
      const charts = await loadCharts(this.dataset.chartsUrl);
      this.chart = charts[this.dataset.chart];
      if (!this.chart) throw new Error('chart not found: ' + this.dataset.chart);
      this.renderTable();
      this.renderMeasureList();
    } catch (error) {
      if (wrap) wrap.innerHTML = '<p class="text-subdued text-sm">Size measurements are unavailable right now.</p>';
      console.warn('[size-guide]', error);
    } finally {
      this.loading = false;
    }
  }

  /* ---------- chart ---------- */

  bindTabs() {
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-table]');
      if (tab) {
        this.table = tab.dataset.table;
        this.querySelectorAll('[data-table]').forEach((node) => {
          const active = node === tab;
          node.classList.toggle('is-active', active);
          node.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        this.renderTable();
        this.renderMeasureList();
        return;
      }

      const unit = event.target.closest('[data-unit]');
      if (unit) {
        this.unit = unit.dataset.unit;
        this.querySelectorAll('[data-unit]').forEach((node) => {
          node.classList.toggle('is-active', node.dataset.unit === this.unit);
        });
        this.renderTable();
        return;
      }
    });
  }

  renderTable() {
    const wrap = this.querySelector('[data-table-wrap]');
    if (!wrap || !this.chart) return;

    const rows = (this.chart.rows && this.chart.rows[this.table]) || Object.keys(this.chart[this.table] || {});
    const data = this.chart[this.table] || {};

    if (!rows.length) {
      wrap.innerHTML = '<p class="text-subdued text-sm">No ' + this.table + ' measurements for this piece.</p>';
      return;
    }

    const head = this.chart.sizes.map((size) => '<th scope="col">' + size + '</th>').join('');
    const body = rows
      .map((name) => {
        const cells = (data[name] || [])
          .map((value) => {
            if (typeof value !== 'number') return '<td>&mdash;</td>';
            const shown = this.unit === 'cm' ? tidy(inToCm(value)) : value;
            return '<td>' + shown + '</td>';
          })
          .join('');
        return '<tr><th scope="row">' + name + '</th>' + cells + '</tr>';
      })
      .join('');

    wrap.innerHTML =
      '<table class="size-guide__table"><thead><tr><th scope="col">' +
      '<span class="size-guide__measure-head">' +
      (this.unit === 'cm' ? 'Measurement (cm)' : 'Measurement (in)') +
      '</span></th>' + head + '</tr></thead><tbody>' + body + '</tbody></table>';

  }

  renderMeasureList() {
    const list = this.querySelector('[data-measure-list]');
    if (!list || !this.chart) return;
    const rows = (this.chart.rows && this.chart.rows[this.table]) || [];
    list.innerHTML = rows
      .filter((name) => MEASURE_HELP[name])
      .map((name) => '<li><span class="size-guide__measure-name">' + name + '</span><span class="size-guide__measure-copy">' + MEASURE_HELP[name] + '</span></li>')
      .join('');
  }

  /* ---------- finder ---------- */

  bindFinder() {
    const finder = this.querySelector('[data-finder]');
    if (!finder) return;

    this.restoreProfile();

    finder.addEventListener('click', (event) => {
      const shape = event.target.closest('.size-guide__shape-option');
      if (shape) {
        const group = shape.closest('[data-shape]');
        if (!group) return;
        group.querySelectorAll('.size-guide__shape-option').forEach((node) => {
          const selected = node === shape;
          node.classList.toggle('is-selected', selected);
          node.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        // Drives which accent arc is shown on the figure.
        group.dataset.selected = shape.dataset.value;
        return;
      }

      const fitOption = event.target.closest('.size-guide__fit');
      if (fitOption) {
        finder.querySelectorAll('.size-guide__fit').forEach((node) => {
          const selected = node === fitOption;
          node.classList.toggle('is-selected', selected);
          node.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        return;
      }

      const next = event.target.closest('[data-next]');
      if (next) {
        if (next.dataset.next === '2' && !this.validateStepOne()) return;
        this.showStep(next.dataset.next);
        return;
      }

      const back = event.target.closest('[data-back]');
      if (back) {
        this.showStep(back.dataset.back);
        return;
      }

      if (event.target.closest('[data-restart]')) {
        this.showStep('1');
        return;
      }

      if (event.target.closest('[data-submit]')) this.submit();
    });

    finder.querySelectorAll('[data-height-unit], [data-weight-unit]').forEach((button) => {
      button.addEventListener('click', () => this.switchUnit(button));
    });

    // Whole numbers only: strip anything that is not a digit as it is typed, so
    // a decimal point, minus sign or stray letter simply never appears. This
    // covers paste and desktop keyboards, not just the mobile numeric keypad.
    finder.querySelectorAll('[data-height-cm], [data-height-ft], [data-height-in], [data-weight]')
      .forEach((input) => {
        input.addEventListener('input', () => {
          const digitsOnly = input.value.replace(/\D/g, '');
          if (digitsOnly !== input.value) input.value = digitsOnly;
        });
      });
  }

  switchUnit(button) {
    if ('heightUnit' in button.dataset) this.switchHeightUnit(button.dataset.heightUnit);
    else this.switchWeightUnit(button.dataset.weightUnit);
  }

  switchHeightUnit(next) {
    if (next === this.heightUnit) return;

    const cmField = this.querySelector('[data-height-cm]');
    const ftField = this.querySelector('[data-height-ft]');
    const inField = this.querySelector('[data-height-in]');

    // Carry the entered height across, rather than just relabelling it.
    if (next === 'ftin') {
      const cm = parseInteger(cmField.value);
      if (cm !== null) {
        const { feet, inches } = cmToFeetInches(cm);
        ftField.value = String(feet);
        inField.value = String(inches);
      }
    } else {
      const feet = parseInteger(ftField.value);
      if (feet !== null) {
        const inches = parseInteger(inField.value) ?? 0;
        cmField.value = String(Math.round(feetInchesToCm(feet, inches)));
      }
    }

    this.heightUnit = next;
    this.querySelector('[data-height-metric]').hidden = next === 'ftin';
    this.querySelector('[data-height-imperial]').hidden = next !== 'ftin';
    this.querySelectorAll('[data-height-unit]').forEach((node) => {
      node.classList.toggle('is-active', node.dataset.heightUnit === next);
    });
  }

  switchWeightUnit(next) {
    if (next === this.weightUnit) return;

    const field = this.querySelector('[data-weight]');
    const value = parseInteger(field.value);
    if (value !== null) {
      field.value = String(Math.round(next === 'lb' ? kgToLb(value) : lbToKg(value)));
    }

    this.weightUnit = next;
    this.querySelectorAll('[data-weight-unit]').forEach((node) => {
      node.classList.toggle('is-active', node.dataset.weightUnit === next);
    });
  }

  validateStepOne() {
    const hint = this.querySelector('[data-hint]');
    const value = (selector) => this.querySelector(selector)?.value ?? '';

    const result = validateMeasurements({
      heightUnit: this.heightUnit,
      heightCm: value('[data-height-cm]'),
      heightFt: value('[data-height-ft]'),
      heightIn: value('[data-height-in]'),
      weightUnit: this.weightUnit,
      weight: value('[data-weight]')
    });

    if (!result.ok) {
      if (hint) {
        hint.textContent = result.message;
        hint.hidden = false;
      }
      return false;
    }
    if (hint) hint.hidden = true;
    this.measurements = result;
    return true;
  }

  showStep(step) {
    this.querySelectorAll('[data-step]').forEach((node) => {
      node.hidden = node.dataset.step !== step;
    });
  }

  selectedValue(selector, fallback) {
    const node = this.querySelector(selector + ' .is-selected');
    return node ? node.dataset.value : fallback;
  }

  submit() {
    // Without chart data there is nothing to compute — say so rather than
    // leaving the button looking dead.
    if (!this.chart) {
      this.renderResult({ error: 'no-chart' });
      this.showStep('result');
      return;
    }
    if (!this.validateStepOne()) {
      this.showStep('1');
      return;
    }

    const chestShape = this.selectedValue('[data-shape="chest"]', 'medium');
    const abdomenShape = this.selectedValue('[data-shape="abdomen"]', 'medium');
    const fit = this.selectedValue('[data-fit]', 'regular');

    // Pipe-separated so a size label containing a comma cannot split into
    // phantom sizes.
    const availableSizes = (this.dataset.sizes || '')
      .split('|')
      .map((value) => value.trim())
      .filter(Boolean);

    const outcome = recommendSize(this.chart, {
      heightCm: this.measurements.heightCm,
      weightKg: this.measurements.weightKg,
      chestShape,
      abdomenShape,
      fit,
      availableSizes
    });

    // Stored in canonical units so it restores correctly whichever unit the
    // customer had selected, on this product or the next.
    this.profile = {
      heightCm: Math.round(this.measurements.heightCm),
      weightKg: Math.round(this.measurements.weightKg),
      heightUnit: this.heightUnit,
      weightUnit: this.weightUnit,
      chestShape,
      abdomenShape,
      fit
    };
    writeProfile(this.profile);

    this.renderResult(outcome);
    this.showStep('result');
  }

  renderResult(outcome) {
    const target = this.querySelector('[data-result]');
    if (!target) return;

    if (outcome.error) {
      target.innerHTML = '<p class="size-guide__result-note">We can\'t suggest a size for this piece. The measurements above are the best guide.</p>';
      return;
    }

    // Just the size. The reasoning behind it is noise at the moment of decision.
    target.innerHTML =
      '<p class="size-guide__result-label">Your size</p>' +
      '<p class="size-guide__result-size">' + outcome.size + '</p>';
  }

  restoreProfile() {
    const profile = this.profile;
    if (!profile || !profile.heightCm) return;

    this.heightUnit = profile.heightUnit === 'ftin' ? 'ftin' : 'cm';
    this.weightUnit = profile.weightUnit === 'lb' ? 'lb' : 'kg';

    const cmField = this.querySelector('[data-height-cm]');
    const ftField = this.querySelector('[data-height-ft]');
    const inField = this.querySelector('[data-height-in]');
    const weightField = this.querySelector('[data-weight]');

    if (cmField) cmField.value = String(profile.heightCm);
    if (ftField && inField) {
      const { feet, inches } = cmToFeetInches(profile.heightCm);
      ftField.value = String(feet);
      inField.value = String(inches);
    }
    if (weightField) {
      weightField.value = String(this.weightUnit === 'lb'
        ? Math.round(kgToLb(profile.weightKg))
        : profile.weightKg);
    }

    const metric = this.querySelector('[data-height-metric]');
    const imperial = this.querySelector('[data-height-imperial]');
    if (metric) metric.hidden = this.heightUnit === 'ftin';
    if (imperial) imperial.hidden = this.heightUnit !== 'ftin';

    this.querySelectorAll('[data-height-unit]').forEach((node) => {
      node.classList.toggle('is-active', node.dataset.heightUnit === this.heightUnit);
    });
    this.querySelectorAll('[data-weight-unit]').forEach((node) => {
      node.classList.toggle('is-active', node.dataset.weightUnit === this.weightUnit);
    });

    const select = (groupSelector, value) => {
      if (!value) return;
      this.querySelectorAll(groupSelector + ' [data-value]').forEach((node) => {
        const selected = node.dataset.value === value;
        node.classList.toggle('is-selected', selected);
        node.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      const group = this.querySelector(groupSelector);
      if (group && group.dataset.selected !== undefined) group.dataset.selected = value;
    };
    select('[data-shape="chest"]', profile.chestShape);
    select('[data-shape="abdomen"]', profile.abdomenShape);
    select('[data-fit]', profile.fit);
  }
}

if (typeof window !== 'undefined' && window.customElements && !window.customElements.get('size-guide')) {
  window.customElements.define('size-guide', SizeGuide);
}
